<template>
    <NumberFieldRoot
        :id="controlId"
        v-model="model"
        :min="min"
        :max="max"
        :step="step"
        :step-snapping="stepSnapping"
        :format-options="formatOptions"
        :locale="locale"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :disable-wheel-change="true"
        :class="[classes, $attrs.class]"
        :style="$attrs.style"
    >
        <template #default="{ textValue }">
            <div :class="element('box')">
                <span
                    v-if="$slots.prefix"
                    :class="element('affix', modifier('start'))"
                    aria-hidden="true"
                >
                    <slot name="prefix" />
                </span>

                <button
                    v-if="steppers"
                    type="button"
                    tabindex="-1"
                    :class="element('step', modifier('decrement'))"
                    :aria-label="decrementLabel"
                    :disabled="decrementDisabled || undefined"
                    :data-pressed="decrementPressed || undefined"
                    @mousedown.prevent="pressStep('decrement')"
                    @mouseup="releaseStep('decrement')"
                    @mouseleave="releaseStep('decrement')"
                    @keydown.space.prevent="pressStep('decrement')"
                    @keyup.space.prevent="triggerStep('decrement')"
                    @keydown.enter.prevent
                    @keyup.enter.prevent="triggerStep('decrement')"
                    @blur="releaseStep('decrement')"
                >
                    <slot name="decrement">
                        <svg
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 10h10"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                            />
                        </svg>
                    </slot>
                </button>

                <NumberFieldInput
                    :ref="setInputRef"
                    :class="element('input')"
                    v-bind="{ ...controlAttrs, ...inputAttrs }"
                    :aria-valuetext="formatOptions ? textValue : undefined"
                />

                <button
                    v-if="steppers"
                    type="button"
                    tabindex="-1"
                    :class="element('step', modifier('increment'))"
                    :aria-label="incrementLabel"
                    :disabled="incrementDisabled || undefined"
                    :data-pressed="incrementPressed || undefined"
                    @mousedown.prevent="pressStep('increment')"
                    @mouseup="releaseStep('increment')"
                    @mouseleave="releaseStep('increment')"
                    @keydown.space.prevent="pressStep('increment')"
                    @keyup.space.prevent="triggerStep('increment')"
                    @keydown.enter.prevent
                    @keyup.enter.prevent="triggerStep('increment')"
                    @blur="releaseStep('increment')"
                >
                    <slot name="increment">
                        <svg
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                        >
                            <path
                                d="M10 5v10M5 10h10"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                            />
                        </svg>
                    </slot>
                </button>

                <span
                    v-if="$slots.suffix"
                    :class="element('affix', modifier('end'))"
                >
                    <slot name="suffix" />
                </span>
            </div>
        </template>
    </NumberFieldRoot>
</template>

<script setup lang="ts">
import type { KNumberFieldProps } from './types'
import useBem from '@yeeroen/use-bem'
import { NumberFieldInput, NumberFieldRoot } from 'reka-ui'
import { computed, nextTick, onBeforeUnmount, ref, useAttrs } from 'vue'
import { useFieldControl } from '../field/use-field-control'

defineOptions({ name: 'KNumberField', inheritAttrs: false })

const props = withDefaults(defineProps<KNumberFieldProps>(), {
    step: 1,
    stepSnapping: true,
    steppers: true,
    incrementLabel: 'Increase',
    decrementLabel: 'Decrease',
    fullWidth: true,
    readonly: false,
    // Left undefined on purpose: `useFieldControl` falls back to the KField context with
    // `??`, and a `false` default would win over an enclosing disabled field.
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    increment?: () => unknown
    decrement?: () => unknown
    prefix?: () => unknown
    suffix?: () => unknown
}>()

const model = defineModel<number | undefined>({ default: undefined })

const attrs = useAttrs()
const { block, element, modifier } = useBem('k-number-field')

const { controlId, controlAttrs, invalid, disabled, size } = useFieldControl(props)

// class/style land on the root (`role="group"`) — a caller adding a class means the box,
// not the inner text node. Everything else falls through to the focusable input: `id` and
// `name` are stripped because they are driven by the field context (`id`) and suppressed
// (`name`) rather than forwarded. `aria-valuetext` is set explicitly below.
const inputAttrs = computed(() => {
    const { id: _id, name: _name, class: _class, style: _style, ...rest } = attrs

    return rest
})

const classes = computed(() => block(
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
    modifier('readonly', props.readonly),
    modifier('full-width', props.fullWidth),
))

// The steppers are native buttons driving the input's own ArrowUp/ArrowDown handling.
// Reka binds `keydown` directly on NumberFieldInput, so dispatching the key applies the
// root's clamping and step-snapping exactly as if the user pressed it. Driving reka's
// NumberFieldIncrement/Decrement parts is unsound twice over: a call site's `@click` would
// replace reka's internal `stop` handler via mergeProps rather than chain, and its
// `usePressedHold` binds to `window` (its current element is unmounted at setup), which
// happy-dom does not reach from a detached tree. Native buttons + key routing avoid both,
// and keep responsibility for the value with the control the user interacts with.
const inputEl = ref<HTMLInputElement>()

function setInputRef(value: unknown) {
    if (value === null)
        return

    const el = (value as { $el?: HTMLInputElement }).$el

    if (el)
        inputEl.value = el
}

const incrementPressed = ref(false)
const decrementPressed = ref(false)

// Mirror reka's per-side disabled computation (clamped at min/max, plus root state), so the
// visible buttons and the behaviour refs stay in lockstep. min/max/step come from props and
// snapping follows reka's `snapValueToStep` semantics closely enough for a disabled state.
const incrementDisabled = computed(() => isStepperSideDisabled('increment'))
const decrementDisabled = computed(() => isStepperSideDisabled('decrement'))

function isStepperSideDisabled(type: 'increment' | 'decrement') {
    if (disabled.value || props.readonly)
        return true

    const value = model.value

    if (value === undefined || Number.isNaN(value))
        return false

    const limit = type === 'increment' ? props.max : props.min

    return limit !== undefined && (type === 'increment' ? value >= limit : value <= limit)
}

let holdTimer: ReturnType<typeof setTimeout> | undefined
let holdInterval: ReturnType<typeof setInterval> | undefined

// A single step, through reka's keydown handling on the input so the root's clamping and
// step-snapping apply. ArrowUp increments, ArrowDown decrements, one `step` at a time.
function stepOnce(type: 'increment' | 'decrement') {
    const key = type === 'increment' ? 'ArrowUp' : 'ArrowDown'

    inputEl.value?.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true }))
}

function pressStep(type: 'increment' | 'decrement') {
    if (isStepperSideDisabled(type))
        return

    setPressed(type, true)
    stepOnce(type)

    // Hold-to-repeat, mirroring reka's `usePressedHold` timing (initial delay, then repeat).
    releaseHold()
    holdTimer = setTimeout(() => {
        holdInterval = setInterval(stepOnce, 60, type)
    }, 400)
}

function triggerStep(type: 'increment' | 'decrement') {
    if (isStepperSideDisabled(type))
        return

    // Keyboard activation fires once per press; the hold is pointer-only, matching native
    // steppers and reka's own buttons.
    stepOnce(type)
    setPressed(type, false)
}

function releaseStep(type: 'increment' | 'decrement') {
    releaseHold()
    setPressed(type, false)
}

function setPressed(type: 'increment' | 'decrement', value: boolean) {
    if (type === 'increment')
        incrementPressed.value = value
    else decrementPressed.value = value
}

function releaseHold() {
    if (holdTimer !== undefined) {
        clearTimeout(holdTimer)
        holdTimer = undefined
    }

    if (holdInterval !== undefined) {
        clearInterval(holdInterval)
        holdInterval = undefined
    }
}

// The mouse/key/blur handlers cover a released press, but not a press that unmounts the
// field (a step updating parent state, navigation) — without this the interval would keep
// firing into the detached input forever.
onBeforeUnmount(releaseHold)

async function focus() {
    await nextTick()
    inputEl.value?.focus()
}

defineExpose({ focus })
</script>

<style lang="scss">
@use './k-number-field.scss';
</style>
